import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["form", "loading", "results", "error"]

  connect() {
    // Auto-submit form on step 2 (data discovery)
    if (this.hasFormTarget && this.formTarget.action.includes('step2')) {
      this.startDataDiscovery()
    }
  }

  async startDataDiscovery() {
    this.showLoading("Discovering company data...")
    
    try {
      const formData = new FormData(this.formTarget)
      const response = await fetch('/companies/creation/discover_data', {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })
      
      const data = await response.json()
      
      if (data.success) {
        this.displayDiscoveryResults(data.data)
        // Redirect to step 3 after a short delay
        setTimeout(() => {
          window.location.href = '/companies/creation/step3'
        }, 2000)
      } else {
        this.showError("Data discovery failed: " + (data.errors ? data.errors.join(', ') : 'Unknown error'))
      }
    } catch (error) {
      this.showError("Data discovery failed: " + error.message)
    }
  }

  async enrichWithApollo() {
    this.showLoading("Enriching with Apollo...")
    
    try {
      const formData = new FormData(this.formTarget)
      const response = await fetch('/companies/creation/enrich_apollo', {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })
      
      const data = await response.json()
      
      if (data.success) {
        this.displayApolloResults(data.data)
      } else {
        this.showError("Apollo enrichment failed: " + data.error)
      }
    } catch (error) {
      this.showError("Apollo enrichment failed: " + error.message)
    }
  }

  async analyzeBlogs() {
    this.showLoading("Analyzing blog content...")
    
    try {
      const formData = new FormData(this.formTarget)
      const response = await fetch('/companies/creation/analyze_blogs', {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })
      
      const data = await response.json()
      
      if (data.success) {
        this.displayBlogResults(data.data)
      } else {
        this.showError("Blog analysis failed: " + data.error)
      }
    } catch (error) {
      this.showError("Blog analysis failed: " + error.message)
    }
  }

  displayDiscoveryResults(data) {
    if (this.hasResultsTarget) {
      this.resultsTarget.innerHTML = `
        <div class="bg-green-50 border border-green-200 rounded-md p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-green-800">Data Discovery Complete</h3>
              <div class="mt-2 text-sm text-green-700">
                <p>Found ${data.data_sources ? data.data_sources.length : 0} data sources with ${data.confidence_score || 0}% confidence</p>
              </div>
            </div>
          </div>
        </div>
      `
    }
  }

  displayApolloResults(data) {
    if (this.hasResultsTarget) {
      this.resultsTarget.innerHTML = `
        <div class="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-blue-800">Apollo Enrichment Complete</h3>
              <div class="mt-2 text-sm text-blue-700">
                <p>Apollo ID: ${data.apollo_id || 'N/A'}</p>
                <p>Founded Year: ${data.founded_year || 'N/A'}</p>
                <p>Industry: ${data.industry || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>
      `
    }
  }

  displayBlogResults(data) {
    if (this.hasResultsTarget) {
      this.resultsTarget.innerHTML = `
        <div class="bg-purple-50 border border-purple-200 rounded-md p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-purple-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-purple-800">Blog Analysis Complete</h3>
              <div class="mt-2 text-sm text-purple-700">
                <p>Found ${data.total_posts || 0} blog posts</p>
                <p>Latest: ${data.latest_post ? data.latest_post.title : 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>
      `
    }
  }

  showLoading(message) {
    if (this.hasLoadingTarget) {
      this.loadingTarget.innerHTML = `
        <div class="flex items-center justify-center p-4">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          <span class="ml-2 text-sm text-gray-600">${message}</span>
        </div>
      `
    }
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.innerHTML = `
        <div class="bg-red-50 border border-red-200 rounded-md p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-red-800">Error</h3>
              <div class="mt-2 text-sm text-red-700">
                <p>${message}</p>
              </div>
            </div>
          </div>
        </div>
      `
    }
  }
}
